<template>
  <ModalComponent
    ref="modalRef"
    submitButtonText="Close"
    :hideSubmitButton="true"
    :dismissible="true"
    size="xl"
    @close="reset"
  >
    <template #title>
      {{ selectedMonth?.year }}-{{
        String(selectedMonth?.month).padStart(2, "0")
      }}
      Financial Breakdown
    </template>
    <template #body>
      <div v-if="selectedMonth" class="p-3">
        <!-- Summary Cards -->
        <div class="row g-3 mb-4">
          <div class="col">
            <div class="card border-success h-100">
              <div class="card-body text-center">
                <h6 class="text-muted mb-1">Gross Revenue</h6>
                <h4 class="text-success mb-0">
                  {{ formatMoney(selectedMonth.gross_revenue_settled) }} €
                </h4>
                <small
                  v-if="selectedMonth.gross_revenue_pending !== 0"
                  class="text-success fst-italic"
                >
                  + {{ formatMoney(selectedMonth.gross_revenue_pending) }}
                  € pending
                </small>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="card border-danger h-100">
              <div class="card-body text-center">
                <h6 class="text-muted mb-1">Costs</h6>
                <h4 class="text-danger mb-0">
                  {{ formatMoney(selectedMonth.costs_paid) }} €
                  <span
                    v-if="selectedMonth.gross_revenue > 0"
                    class="text-muted fs-6 fw-normal"
                  >
                    ({{
                      pct(
                        selectedMonth.costs_paid,
                        selectedMonth.gross_revenue
                      )
                    }}%)
                  </span>
                </h4>
                <small
                  v-if="selectedMonth.costs_pending > 0"
                  class="text-warning"
                >
                  + {{ formatMoney(selectedMonth.costs_pending) }} € pending ({{
                    pct(
                      selectedMonth.costs_pending,
                      selectedMonth.gross_revenue
                    )
                  }}%)
                </small>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="card border-purple h-100">
              <div class="card-body text-center">
                <h6 class="text-muted mb-1">
                  Referral
                  <span
                    v-if="
                      selectedMonth.expected_referral >
                      selectedMonth.referral_bonus
                    "
                    class="badge bg-secondary bg-opacity-25 text-purple"
                    >est.</span
                  >
                </h6>
                <h4 class="text-purple mb-0">
                  {{ formatMoney(selectedMonth.referral_settled) }} €
                </h4>
                <small
                  v-if="selectedMonth.referral_pending !== 0"
                  class="text-purple fst-italic"
                >
                  + {{ formatMoney(selectedMonth.referral_pending) }} € pending
                </small>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="card border-info h-100">
              <div class="card-body text-center">
                <h6 class="text-muted mb-1">Net Revenue</h6>
                <h4 class="text-info mb-0">
                  {{ formatMoney(selectedMonth.net_revenue_settled) }} €
                  <span
                    v-if="selectedMonth.gross_revenue_settled > 0"
                    class="text-muted fs-6 fw-normal"
                  >
                    ({{
                      pct(
                        selectedMonth.net_revenue_settled,
                        selectedMonth.gross_revenue_settled
                      )
                    }}%)
                  </span>
                </h4>
                <small
                  v-if="selectedMonth.net_revenue_pending !== 0"
                  class="text-info fst-italic d-block"
                >
                  {{ selectedMonth.net_revenue_pending > 0 ? "+" : "" }}
                  {{ formatMoney(selectedMonth.net_revenue_pending) }} € pending
                </small>
                <small class="text-muted">Gross - Costs</small>
              </div>
            </div>
          </div>
          <div class="col">
            <div class="card border-primary h-100">
              <div class="card-body text-center">
                <h6 class="text-muted mb-1">After Referral</h6>
                <h4 class="text-primary mb-0">
                  {{ formatMoney(selectedMonth.net_after_referral_settled) }}
                  €
                  <span
                    v-if="selectedMonth.gross_revenue_settled > 0"
                    class="text-muted fs-6 fw-normal"
                  >
                    ({{
                      pct(
                        selectedMonth.net_after_referral_settled,
                        selectedMonth.gross_revenue_settled
                      )
                    }}%)
                  </span>
                </h4>
                <small
                  v-if="selectedMonth.net_after_referral_pending !== 0"
                  class="text-primary fst-italic d-block"
                >
                  {{ selectedMonth.net_after_referral_pending > 0 ? "+" : "" }}
                  {{ formatMoney(selectedMonth.net_after_referral_pending) }}
                  € pending
                </small>
                <small class="text-muted">Net - Referral</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Net revenue by source: settled / pending per stream, reconciling to the month's net revenue -->
        <div class="card mb-4">
          <div
            class="card-header d-flex justify-content-between align-items-center cursor-pointer"
            @click="streamsExpanded = !streamsExpanded"
          >
            <strong>Net revenue by source</strong>
            <div class="d-flex gap-3 align-items-center small">
              <span
                v-for="group in STREAM_GROUP_ORDER"
                :key="group"
                :class="groupTextClass(group)"
                :title="STREAM_GROUP_LABELS[group]"
              >
                {{ STREAM_GROUP_LABELS[group] }}:
                <strong
                  >{{
                    formatMoney(
                      groupTotal(selectedMonth.streams_settled, streams, group)
                    )
                  }}
                  €</strong
                >
              </span>
              <font-awesome-icon
                :icon="streamsExpanded ? 'chevron-up' : 'chevron-down'"
              />
            </div>
          </div>
          <div v-if="streamsExpanded" class="card-body p-0">
            <table class="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Stream</th>
                  <th>Group</th>
                  <th class="text-end">Settled</th>
                  <th class="text-end">Pending</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stream in visibleStreams" :key="stream.key">
                  <td>{{ stream.label }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="groupBadgeClass(stream.group)"
                      >{{ STREAM_GROUP_LABELS[stream.group] }}</span
                    >
                  </td>
                  <td
                    class="text-end"
                    :class="{ 'text-danger': stream.group === 'adjustment' }"
                  >
                    {{
                      streamAmount(
                        stream,
                        selectedMonth.streams_settled[stream.key]
                      )
                    }}
                    €
                  </td>
                  <td class="text-end fst-italic text-muted">
                    {{
                      streamAmount(
                        stream,
                        selectedMonth.streams_pending[stream.key]
                      )
                    }}
                    €
                  </td>
                  <td
                    class="text-end fw-semibold"
                    :class="{ 'text-danger': stream.group === 'adjustment' }"
                  >
                    {{
                      streamAmount(
                        stream,
                        selectedMonth.streams_settled[stream.key] +
                          selectedMonth.streams_pending[stream.key]
                      )
                    }}
                    €
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-light fw-bold">
                  <td colspan="2">Net revenue</td>
                  <td class="text-end">
                    {{
                      formatMoney(netFromStreams(selectedMonth.streams_settled))
                    }}
                    €
                  </td>
                  <td class="text-end fst-italic">
                    {{
                      formatMoney(netFromStreams(selectedMonth.streams_pending))
                    }}
                    €
                  </td>
                  <td class="text-end">
                    {{
                      formatMoney(
                        netFromStreams(selectedMonth.streams_settled) +
                          netFromStreams(selectedMonth.streams_pending)
                      )
                    }}
                    €
                    <span
                      v-if="
                        Math.abs(
                          netFromStreams(selectedMonth.streams_settled) +
                            netFromStreams(selectedMonth.streams_pending) -
                            selectedMonth.net_revenue
                        ) > 0.05
                      "
                      class="badge bg-danger ms-1"
                      title="Streams do not reconcile to net revenue"
                    >
                      ≠ {{ formatMoney(selectedMonth.net_revenue) }} €
                    </span>
                    <span
                      v-else
                      class="badge bg-success bg-opacity-25 text-success ms-1"
                      title="Streams reconcile to net revenue"
                      >✓ reconciles</span
                    >
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Loading state for detail -->
        <LoadingIndicatorComponent
          v-if="isLoading"
          message="Loading transaction details..."
        />

        <!-- Transaction-Grouped Breakdown -->
        <div v-else-if="detail" class="mt-2">
          <h6 class="fw-bold mb-3">
            Month Breakdown: {{ selectedMonth.year }}-{{
              String(selectedMonth.month).padStart(2, "0")
            }}
            <span class="badge bg-secondary ms-2">
              {{ detail.other_income_items.length }} non-tx items |
              {{ detail.transactions.length }} transactions
            </span>
          </h6>

          <!-- Section 1: Items with NO sales transaction -->
          <div v-if="detail.other_income_items.length > 0" class="mb-4">
            <div class="card border-warning">
              <div
                class="card-header bg-warning bg-opacity-10 d-flex justify-content-between align-items-center cursor-pointer"
                @click="toggleNoTxExpanded()"
              >
                <div>
                  <strong>Other Income (No Sales Transaction)</strong>
                  <span class="badge bg-warning text-dark ms-2">
                    {{ detail.other_income_items.length }} items
                  </span>
                </div>
                <div class="d-flex gap-3 align-items-center small">
                  <span class="text-success" title="Gross Revenue (settled)">
                    <strong>
                      {{ formatMoney(otherIncomeBreakdown.gross_revenue) }} €
                    </strong>
                  </span>
                  <span
                    v-if="otherIncomeBreakdown.gross_revenue_pending > 0"
                    class="text-success fst-italic"
                    title="Gross Revenue (pending)"
                  >
                    ~{{
                      formatMoney(otherIncomeBreakdown.gross_revenue_pending)
                    }}
                    €
                  </span>
                  <span class="text-danger" title="Costs (paid)">
                    -{{ formatMoney(otherIncomeBreakdown.costs_paid) }} €
                    <span
                      v-if="otherIncomeBreakdown.gross_revenue > 0"
                      class="text-muted"
                    >
                      ({{
                        pct(
                          otherIncomeBreakdown.costs_paid,
                          otherIncomeBreakdown.gross_revenue
                        )
                      }}%)
                    </span>
                  </span>
                  <span class="text-info fw-bold" title="Net Revenue">
                    = {{ formatMoney(otherIncomeBreakdown.net_revenue) }} €
                    <span
                      v-if="otherIncomeBreakdown.gross_revenue > 0"
                      class="text-muted fw-normal"
                    >
                      ({{
                        pct(
                          otherIncomeBreakdown.net_revenue,
                          otherIncomeBreakdown.gross_revenue
                        )
                      }}%)
                    </span>
                  </span>
                  <font-awesome-icon
                    :icon="noTxExpanded ? 'chevron-up' : 'chevron-down'"
                  />
                </div>
              </div>
              <div v-if="noTxExpanded" class="card-body p-0">
                <DataTable
                  :value="detail.other_income_items"
                  scrollable
                  :scrollHeight="
                    detail.other_income_items.length > 14 ? '600px' : undefined
                  "
                  :virtualScrollerOptions="
                    detail.other_income_items.length > 14
                      ? { itemSize: 42, numToleratedItems: 20 }
                      : undefined
                  "
                  filterDisplay="row"
                  v-model:filters="otherIncomeFilters"
                  class="p-datatable-sm border rounded"
                >
                  <Column
                    field="id"
                    header="ID"
                    sortable
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '80px' }"
                  >
                    <template #body="{ data }">
                      <span class="badge text-bg-secondary">{{ data.id }}</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="ID"
                      />
                    </template>
                  </Column>
                  <Column
                    field="item_type"
                    header="Type"
                    sortable
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '240px' }"
                  >
                    <template #body="{ data }">
                      <span
                        class="badge me-1"
                        :class="{
                          'bg-success': data.category === 'margin',
                          'bg-danger': data.category === 'cost',
                          'bg-warning': data.category === 'referral',
                          'bg-info': data.category === 'pass_through',
                          'bg-secondary': data.category === 'other',
                        }"
                      >
                        {{ data.category }}
                      </span>
                      <code class="small">{{ data.item_type }}</code>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Filter type"
                      />
                    </template>
                  </Column>
                  <Column
                    field="description"
                    header="Description"
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '250px' }"
                  >
                    <template #body="{ data }">
                      <span class="small">{{ data.description || "-" }}</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Filter"
                      />
                    </template>
                  </Column>
                  <Column
                    field="amount"
                    header="MWh"
                    sortable
                    :style="{ width: '80px' }"
                  >
                    <template #body="{ data }">
                      {{ data.amount?.toLocaleString() }}
                    </template>
                  </Column>
                  <Column
                    field="unit_price"
                    header="€/MWh"
                    sortable
                    :style="{ width: '80px' }"
                  >
                    <template #body="{ data }">
                      {{ data.unit_price?.toFixed(4) }}
                    </template>
                  </Column>
                  <Column
                    field="total"
                    header="Total €"
                    sortable
                    :style="{ width: '100px' }"
                  >
                    <template #body="{ data }">
                      <span
                        :class="
                          data.unit_price < 0 ? 'text-danger' : 'text-success'
                        "
                      >
                        {{ formatMoney(data.total) }} €
                      </span>
                    </template>
                  </Column>
                  <Column
                    field="fee_invoice_number"
                    header="Fee Invoice"
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '200px' }"
                  >
                    <template #body="{ data }">
                      <a
                        v-if="data.fee_invoice_id"
                        href="#"
                        class="small text-decoration-none"
                        title="Download invoice PDF"
                        @click.prevent="downloadInvoice(data.fee_invoice_id)"
                      >
                        {{ data.fee_invoice_number }}
                      </a>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Invoice #"
                      />
                    </template>
                  </Column>
                  <Column
                    field="paid_at"
                    header="Paid"
                    sortable
                    :style="{ width: '130px' }"
                  >
                    <template #body="{ data }">
                      <template v-if="data.paid_at">
                        {{ data.paid_at.split("T")[0] }}
                      </template>
                      <span v-else class="badge bg-warning text-dark">
                        pending
                      </span>
                    </template>
                  </Column>
                </DataTable>
              </div>
            </div>
          </div>

          <!-- Section 2: Sales Transactions -->
          <div v-for="tx in detail.transactions" :key="tx.id" class="mb-3">
            <div class="card">
              <div
                class="card-header d-flex justify-content-between align-items-center cursor-pointer"
                @click="toggleTxExpanded(tx.id)"
              >
                <div>
                  <a
                    :href="`/admin/transactions/${tx.id}/edit`"
                    target="_blank"
                    class="badge bg-info text-decoration-none me-2"
                    @click.stop
                  >
                    TX #{{ tx.id }}
                  </a>
                  <strong>{{ tx.counterparty }}</strong>
                  <span v-if="tx.status" class="badge bg-secondary ms-2 small">
                    {{ tx.status }}
                  </span>
                  <span
                    v-if="tx.gross_revenue_pending > 0"
                    class="badge bg-warning text-dark ms-2 small"
                  >
                    pending
                  </span>
                </div>
                <div class="d-flex flex-column align-items-end gap-1">
                  <div class="d-flex gap-3 align-items-center small">
                    <span class="text-success" title="Gross Revenue (settled)">
                      <strong>
                        {{ formatMoney(tx.gross_revenue_settled) }} €
                      </strong>
                    </span>
                    <span
                      v-if="tx.gross_revenue_pending !== 0"
                      class="text-success fst-italic"
                      title="Gross Revenue (pending)"
                    >
                      ~{{ formatMoney(tx.gross_revenue_pending) }} €
                    </span>
                    <span
                      v-if="tx.costs_paid > 0"
                      class="text-danger"
                      title="Costs (paid)"
                    >
                      -{{ formatMoney(tx.costs_paid) }} €
                      <span v-if="tx.gross_revenue > 0" class="text-muted">
                        ({{ pct(tx.costs_paid, tx.gross_revenue) }}%)
                      </span>
                    </span>
                    <span
                      v-if="tx.costs_pending > 0"
                      class="text-danger fst-italic"
                      title="Costs (pending)"
                    >
                      ~{{ formatMoney(tx.costs_pending) }} €
                      <span v-if="tx.gross_revenue > 0" class="text-muted">
                        ({{ pct(tx.costs_pending, tx.gross_revenue) }}%)
                      </span>
                    </span>
                    <span
                      v-if="tx.net_revenue_settled !== 0"
                      class="text-info fw-bold"
                      title="Net Revenue (settled)"
                    >
                      = {{ formatMoney(tx.net_revenue_settled) }} €
                      <span
                        v-if="tx.gross_revenue > 0"
                        class="text-muted fw-normal"
                      >
                        ({{ pct(tx.net_revenue_settled, tx.gross_revenue) }}%)
                      </span>
                    </span>
                    <span
                      v-if="tx.net_revenue_pending !== 0"
                      class="text-info fw-bold fst-italic"
                      title="Net Revenue (pending)"
                    >
                      = ~{{ formatMoney(tx.net_revenue_pending) }} €
                      <span
                        v-if="tx.gross_revenue > 0"
                        class="text-muted fw-normal"
                      >
                        ({{ pct(tx.net_revenue_pending, tx.gross_revenue) }}%)
                      </span>
                    </span>
                    <span
                      v-if="
                        tx.net_revenue_settled === 0 &&
                        tx.net_revenue_pending === 0
                      "
                      class="text-info fw-bold"
                      title="Net Revenue"
                    >
                      = {{ formatMoney(tx.net_revenue) }} €
                    </span>
                    <span
                      v-if="tx.swap_margin"
                      :class="
                        tx.swap_margin >= 0 ? 'text-success' : 'text-danger'
                      "
                      title="Swap margin"
                    >
                      swap {{ formatMoney(tx.swap_margin) }} €
                    </span>
                    <font-awesome-icon
                      :icon="
                        expandedTxIds.has(tx.id) ? 'chevron-up' : 'chevron-down'
                      "
                    />
                  </div>
                  <div
                    v-if="tx.expected_referral > 0"
                    class="d-flex gap-3 align-items-center small"
                  >
                    <span class="text-purple" title="Referral">
                      {{ formatMoney(tx.expected_referral) }} €
                      <span
                        v-if="tx.expected_referral > tx.referral_bonus"
                        class="badge bg-secondary bg-opacity-25 text-secondary"
                        >est.</span
                      >
                    </span>
                    <span
                      v-if="tx.net_after_referral_settled !== 0"
                      class="text-primary fw-bold"
                      title="Net Revenue after Referral (settled)"
                    >
                      → {{ formatMoney(tx.net_after_referral_settled) }} €
                    </span>
                    <span
                      v-if="tx.net_after_referral_pending !== 0"
                      class="text-primary fw-bold fst-italic"
                      title="Net Revenue after Referral (pending)"
                    >
                      → ~{{ formatMoney(tx.net_after_referral_pending) }} €
                    </span>
                    <span
                      v-if="
                        tx.net_after_referral_settled === 0 &&
                        tx.net_after_referral_pending === 0
                      "
                      class="text-primary fw-bold"
                      title="Net Revenue after Referral"
                    >
                      → {{ formatMoney(tx.net_after_referral) }} €
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="expandedTxIds.has(tx.id)" class="card-body p-0">
                <DataTable
                  :value="tx.items"
                  scrollable
                  :scrollHeight="tx.items.length > 14 ? '600px' : undefined"
                  :virtualScrollerOptions="
                    tx.items.length > 14
                      ? { itemSize: 42, numToleratedItems: 20 }
                      : undefined
                  "
                  filterDisplay="row"
                  v-model:filters="txItemFilters"
                  class="p-datatable-sm border rounded"
                >
                  <Column
                    field="id"
                    header="ID"
                    sortable
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '80px' }"
                  >
                    <template #body="{ data }">
                      <span class="badge text-bg-secondary">{{ data.id }}</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="ID"
                      />
                    </template>
                  </Column>
                  <Column
                    field="item_type"
                    header="Type"
                    sortable
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '240px' }"
                  >
                    <template #body="{ data }">
                      <span
                        class="badge me-1"
                        :class="{
                          'bg-success': data.category === 'margin',
                          'bg-danger': data.category === 'cost',
                          'bg-warning': data.category === 'referral',
                          'bg-info': data.category === 'pass_through',
                          'bg-secondary': data.category === 'other',
                        }"
                      >
                        {{ data.category }}
                      </span>
                      <code class="small">{{ data.item_type }}</code>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Filter type"
                      />
                    </template>
                  </Column>
                  <Column
                    field="company_name"
                    header="Company"
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '180px' }"
                  >
                    <template #body="{ data }">
                      <span class="small">{{ data.company_name }}</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Company"
                      />
                    </template>
                  </Column>
                  <Column
                    field="amount"
                    header="MWh"
                    sortable
                    :style="{ width: '80px' }"
                  >
                    <template #body="{ data }">
                      {{ data.amount?.toLocaleString() }}
                    </template>
                  </Column>
                  <Column
                    field="unit_price"
                    header="€/MWh"
                    sortable
                    :style="{ width: '80px' }"
                  >
                    <template #body="{ data }">
                      {{ data.unit_price?.toFixed(4) }}
                    </template>
                  </Column>
                  <Column
                    field="total"
                    header="Total €"
                    sortable
                    :style="{ width: '100px' }"
                  >
                    <template #body="{ data }">
                      <span
                        :class="
                          data.unit_price < 0 ? 'text-danger' : 'text-success'
                        "
                      >
                        {{ formatMoney(data.total) }} €
                      </span>
                    </template>
                  </Column>
                  <Column
                    field="fee_invoice_number"
                    header="Fee Invoice"
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '200px' }"
                  >
                    <template #body="{ data }">
                      <a
                        v-if="data.fee_invoice_id"
                        href="#"
                        class="small text-decoration-none"
                        title="Download invoice PDF"
                        @click.prevent="downloadInvoice(data.fee_invoice_id)"
                      >
                        {{ data.fee_invoice_number }}
                      </a>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Invoice #"
                      />
                    </template>
                  </Column>
                  <Column
                    field="expense_invoice_number"
                    header="Expense Invoice"
                    :showFilterMatchModes="false"
                    :showFilterMenu="false"
                    :style="{ width: '200px' }"
                  >
                    <template #body="{ data }">
                      <a
                        v-if="data.expense_invoice_id"
                        href="#"
                        class="small text-decoration-none"
                        title="Download invoice PDF"
                        @click.prevent="
                          downloadInvoice(data.expense_invoice_id)
                        "
                      >
                        {{ data.expense_invoice_number }}
                      </a>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template #filter="{ filterModel, filterCallback }">
                      <InputText
                        v-model="filterModel.value"
                        type="text"
                        @input="filterCallback()"
                        class="p-column-filter w-100"
                        placeholder="Invoice #"
                      />
                    </template>
                  </Column>
                  <Column
                    field="fee_paid_at"
                    header="Fee Paid"
                    sortable
                    :style="{ width: '100px' }"
                  >
                    <template #body="{ data }">
                      {{
                        data.fee_paid_at ? data.fee_paid_at.split("T")[0] : "-"
                      }}
                    </template>
                  </Column>
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-secondary" @click="modalRef?.toggleModal()">
        Close
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import { useFileDownload } from "@/composables/useFileDownload";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import { computed, ref, useTemplateRef } from "vue";
import { financialDashboardApi } from "./financialDashboardApi";
import {
  STREAM_GROUP_LABELS,
  STREAM_GROUP_ORDER,
  groupTextClass,
  groupTotal,
  netFromStreams,
  pct,
} from "./financialDashboardMetrics";
import type {
  MonthDetail,
  MonthlyData,
  RevenueStreamGroup,
  RevenueStreamInfo,
} from "./financialDashboardTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * Drill-down for one dashboard month: summary cards, net revenue by stream, standalone items and
 * every sales transaction with its invoice lines. Owns its own fetch; open it with `open(month)`.
 * `countryCodes` must be the filter that produced the clicked figures so the modal reconciles.
 */
const props = defineProps<{
  countryCodes: string[];
  streams: RevenueStreamInfo[];
}>();

const { globalErrorHandler } = useGlobalHandlers();
const { downloadInvoice } = useFileDownload();

const modalRef =
  useTemplateRef<InstanceType<typeof ModalComponent>>("modalRef");
const selectedMonth = ref<MonthlyData | null>(null);
const detail = ref<MonthDetail | null>(null);
const isLoading = ref(false);
const streamsExpanded = ref(false);
const expandedTxIds = ref<Set<number>>(new Set());
const noTxExpanded = ref(false);

const txItemFilters = ref({
  id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  item_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
  company_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  fee_invoice_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
  expense_invoice_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

const otherIncomeFilters = ref({
  id: { value: null, matchMode: FilterMatchMode.CONTAINS },
  item_type: { value: null, matchMode: FilterMatchMode.CONTAINS },
  description: { value: null, matchMode: FilterMatchMode.CONTAINS },
  fee_invoice_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
});

/** Streams with any money in the month, so the reconciliation table stays short. */
const visibleStreams = computed(() => {
  const month = selectedMonth.value;
  if (!month) return [];
  return props.streams.filter(
    (stream) =>
      month.streams_settled[stream.key] !== 0 ||
      month.streams_pending[stream.key] !== 0
  );
});

// P&L for items without a sales transaction — mirrors the backend aggregation of other_items +
// correction_items so the section header reconciles with the month totals.
const otherIncomeBreakdown = computed(() => {
  const totals = {
    gross_revenue: 0,
    gross_revenue_pending: 0,
    costs_paid: 0,
    costs_pending: 0,
    net_revenue: 0,
  };
  if (!detail.value) return totals;

  for (const item of detail.value.other_income_items) {
    if (item.category === "referral") {
      // Fee-based referral bonuses only affect net_after_referral, never gross/costs/net_revenue.
      continue;
    }
    if (item.category === "pass_through") {
      totals.gross_revenue += item.total;
      totals.costs_paid += item.total;
    } else if (item.unit_price < 0) {
      if (item.is_pending) totals.costs_pending += item.total;
      else totals.costs_paid += item.total;
      totals.net_revenue -= item.total;
    } else {
      if (item.is_pending) totals.gross_revenue_pending += item.total;
      else totals.gross_revenue += item.total;
      totals.net_revenue += item.total;
    }
  }
  return totals;
});

async function open(month: MonthlyData) {
  selectedMonth.value = month;
  modalRef.value?.toggleModal();
  isLoading.value = true;
  try {
    const response = await financialDashboardApi.getMonthDetail(
      month.year,
      month.month,
      props.countryCodes
    );
    detail.value = response.data;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}

function reset() {
  selectedMonth.value = null;
  detail.value = null;
  expandedTxIds.value.clear();
  noTxExpanded.value = false;
  streamsExpanded.value = false;
}

function toggleTxExpanded(txId: number) {
  if (expandedTxIds.value.has(txId)) {
    expandedTxIds.value.delete(txId);
  } else {
    expandedTxIds.value.add(txId);
  }
}

function toggleNoTxExpanded() {
  noTxExpanded.value = !noTxExpanded.value;
}

/** Corrections are stored positive but reduce net revenue, so they render with a leading minus (never "-0"). */
const streamAmount = (stream: RevenueStreamInfo, amount: number): string =>
  `${stream.group === "adjustment" && amount !== 0 ? "-" : ""}${formatMoney(amount)}`;

const groupBadgeClass = (group: RevenueStreamGroup): string =>
  ({
    go: "bg-primary",
    platform_fee: "bg-success",
    one_off: "bg-warning text-dark",
    adjustment: "bg-danger",
  })[group];

defineExpose({ open });
</script>

<style scoped lang="scss">
.cursor-pointer {
  cursor: pointer;
}

.text-purple {
  color: #7c3aed;
}

.border-purple {
  border-color: #7c3aed !important;
}
</style>
